import React, { useState, useEffect } from 'react';
import api from './api/axios';
import { QRCodeSVG } from 'qrcode.react';

const FTP_TRIVIA_BASE = 'https://ftp.pcnpublicidad.com/boehringer/Trivia_png';

const FocusTrivia = () => {
    // Configuración global (estaciones, visuales)
    const [assignedStation, setAssignedStation] = useState(null);

    // Banco de preguntas de la Trivia
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOptionId, setSelectedOptionId] = useState(null);
    const [userAnswers, setUserAnswers] = useState({});

    // Estado de la trivia: 'idle' | 'playing' | 'completed'
    const [gameState, setGameState] = useState('idle');
    const [timeLeft, setTimeLeft] = useState(120); // 2 minutos (120 segundos)

    // Modal de instrucciones de juego
    const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(true);

    // Cargar configuraciones del backend
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [designsRes, stationsRes, pollsRes] = await Promise.all([
                    api.get('/configs/page_designs').catch(() => ({ data: {} })),
                    api.get('/stations').catch(() => ({ data: [] })),
                    api.get('/polls/all').catch(() => ({ data: [] }))
                ]);

                const designs = designsRes.data || {};
                const activeStations = (stationsRes.data || []).filter(st => st.status === 'active');

                // Obtener ID de la estación configurada para Trivia
                const triviaStationId = designs?.trivia?.stationId || designs?.pageDesigns?.trivia?.stationId;
                if (triviaStationId) {
                    const st = activeStations.find(s => s.id === parseInt(triviaStationId, 10) || s.code === triviaStationId);
                    if (st) setAssignedStation(st);
                }

                // Cargar TODAS las preguntas registradas en Admin (sin filtrar por estado activo)
                let rawPolls = Array.isArray(pollsRes.data) ? pollsRes.data : [];
                console.log("Raw polls response count:", rawPolls.length);

                if (rawPolls.length > 0) {
                    const detailedQuestions = await Promise.all(
                        rawPolls.map(async (p) => {
                            try {
                                const details = await api.get(`/polls/${p.id}`);
                                return details.data;
                            } catch (e) {
                                console.warn(`Error fetching details for poll ${p.id}:`, e);
                                return p;
                            }
                        })
                    );
                    const validQuestions = detailedQuestions.filter(p => p && p.question);
                    console.log("Valid detailed trivia questions loaded:", validQuestions.length);
                    setQuestions(validQuestions);
                }
            } catch (err) {
                console.error("Error al cargar datos iniciales de trivia:", err);
            }
        };

        fetchInitialData();
    }, []);

    // Función para mezclar aleatoriamente el banco de preguntas
    const shuffleArray = (array) => {
        return [...array].sort(() => Math.random() - 0.5);
    };

    // Temporizador regresivo de 2.5 minutos (150s)
    useEffect(() => {
        let timer;
        if (gameState === 'playing' && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        setGameState('completed');
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [gameState, timeLeft]);

    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const prepareAndStartGame = () => {
        if (questions.length === 0) {
            alert('No hay preguntas configuradas en el sistema. Por favor configure las preguntas desde el panel de administración.');
            return;
        }
        // Seleccionar aleatoriamente un máximo de 10 preguntas por ronda
        const shuffled = shuffleArray(questions).slice(0, 10);
        setQuestions(shuffled);
        setCurrentQuestionIndex(0);
        setSelectedOptionId(null);
        setUserAnswers({});
        setTimeLeft(150); // 2.5 minutos
        setIsInstructionsModalOpen(false);
        setGameState('playing');
    };

    const handleRestartClick = () => {
        setIsInstructionsModalOpen(true);
        setGameState('idle');
    };

    // Selección de opción de respuesta
    const handleSelectOption = (optionId) => {
        if (gameState !== 'playing') return;
        setSelectedOptionId(optionId);
        setUserAnswers((prev) => ({
            ...prev,
            [currentQuestionIndex]: optionId
        }));
    };

    // Pasar a la siguiente pregunta
    const handleNextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            const nextIdx = currentQuestionIndex + 1;
            setCurrentQuestionIndex(nextIdx);
            setSelectedOptionId(userAnswers[nextIdx] || null);
        } else {
            // Finalizó todas las preguntas antes de agotar el tiempo
            setGameState('completed');
        }
    };

    const handlePrevQuestion = () => {
        if (currentQuestionIndex > 0) {
            const prevIdx = currentQuestionIndex - 1;
            setCurrentQuestionIndex(prevIdx);
            setSelectedOptionId(userAnswers[prevIdx] || null);
        }
    };

    // Calcular respuestas correctas y puntos acumulados (10 pts por pregunta correcta)
    const calculateResults = () => {
        let correctCount = 0;
        questions.forEach((q, idx) => {
            const selectedOptId = userAnswers[idx];
            if (selectedOptId && q.options) {
                const opt = q.options.find(o => o.id === selectedOptId);
                if (opt && (opt.is_correct === 1 || opt.is_correct === true)) {
                    correctCount++;
                }
            }
        });
        const pointsPerQuestion = 10; // 10 puntos por respuesta correcta
        const totalPoints = correctCount * pointsPerQuestion;
        return { correctCount, totalPoints };
    };

    const results = calculateResults();
    const currentQ = questions[currentQuestionIndex];
    const lettersMap = ['A', 'B', 'C', 'D'];

    // Construcción del Payload para el Código QR final
    // Formato: station=station_id|points=cantidad_puntos (si los puntos son > 0)
    const stationCode = assignedStation ? (assignedStation.code || assignedStation.id) : 1;
    const qrPayload = results.totalPoints > 0
        ? `station=${stationCode}|points=${results.totalPoints}`
        : `station=${stationCode}`;

    return (
        <div
            className="min-h-screen bg-[#07241c] text-white flex flex-col justify-between items-center p-4 relative overflow-hidden font-sans bg-cover bg-center select-none"
            style={{ backgroundImage: `url(${FTP_TRIVIA_BASE}/fondo.png)` }}
        >
            {/* Header de la Trivia con Banners SVG e Insignias */}
            <header className="w-full max-w-6xl flex justify-between items-center pt-2 z-10 px-4">
                {/* Banner Izquierdo de la Trivia */}
                <div className="relative flex items-center">
                    <img
                        src={`${FTP_TRIVIA_BASE}/trivia.png`}
                        alt="TRIVIA"
                        className="h-12 md:h-16 object-contain"
                    />
                </div>

                {/* Reloj de Cuenta Regresiva (2.5 min) */}
                {gameState === 'playing' && (
                    <div className="bg-[#0b382c]/90 border-2 border-[#00e676]/60 px-6 py-2 rounded-2xl flex items-center gap-3 backdrop-blur-md shadow-lg animate-pulse">
                        <span className="text-sm font-bold text-slate-300">⏱️ TIEMPO RESTANTE:</span>
                        <span className="text-xl md:text-2xl font-black text-[#00e676] font-mono">
                            {formatTimer(timeLeft)}
                        </span>
                    </div>
                )}

                {/* Lema Superior Derecho */}
                <div className="flex items-center">
                    <img
                        src={`${FTP_TRIVIA_BASE}/titulo_slogan.png`}
                        alt="¡SOMOS LA FÓRMULA 1!"
                        className="h-10 md:h-14 object-contain"
                    />
                </div>
            </header>

            {/* Pantalla Inicial (Idle) */}
            {gameState === 'idle' && (
                <main className="w-full max-w-2xl flex flex-col items-center justify-center my-auto z-10 text-center space-y-8 bg-[#0b382c]/80 border-2 border-[#00e676]/40 p-8 rounded-3xl backdrop-blur-md shadow-2xl">
                    <div className="space-y-4">
                        <h1 className="text-3xl md:text-4xl font-black text-[#00e676] uppercase tracking-wider">
                            TRIVIA DE CONOCIMIENTO
                        </h1>
                        <p className="text-slate-200 text-sm md:text-base font-medium leading-relaxed">
                            Ponga a prueba sus conocimientos en la trivia interactiva de Boehringer Ingelheim.
                            Dispone de un tiempo límite de <span className="font-bold text-[#00e676]">2 minutos y 30 segundos</span> para responder el banco de preguntas.
                        </p>
                    </div>

                    {assignedStation ? (
                        <div className="bg-[#07241c] border border-[#00e676]/40 px-6 py-3 rounded-2xl text-xs md:text-sm font-bold text-[#00e676]">
                            📍 Estación Asignada: <span className="text-white">{assignedStation.name}</span> ({assignedStation.code})
                        </div>
                    ) : (
                        <div className="bg-amber-950/60 border border-amber-500/40 px-6 py-3 rounded-2xl text-xs font-medium text-amber-300">
                            ⚠️ Nota: No hay una estación asignada en las configuraciones para acumular puntos.
                        </div>
                    )}

                    <button
                        onClick={prepareAndStartGame}
                        className="bg-[#00e676] hover:bg-[#00c853] text-[#07241c] py-4 px-12 rounded-2xl font-black text-lg uppercase tracking-widest shadow-xl hover:scale-105 active:scale-95 transition-all"
                    >
                        INICIAR TRIVIA
                    </button>
                </main>
            )}

            {/* Modal de Instrucciones de Juego */}
            {isInstructionsModalOpen && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-[#0b382c] border-2 border-[#00e676] rounded-3xl p-6 md:p-8 max-w-lg w-full text-center space-y-6 shadow-2xl">
                        <div className="text-5xl">📋</div>
                        <h2 className="text-2xl font-black text-[#00e676] uppercase tracking-wider">
                            INSTRUCCIONES DE TRIVIA
                        </h2>
                        <div className="bg-[#07241c] border border-[#00e676]/30 p-4 rounded-2xl text-xs md:text-sm text-slate-200 space-y-3 text-left shadow-inner font-medium">
                            <div className="flex items-start gap-2">
                                <span className="text-[#00e676] font-bold">•</span>
                                <span>En cada ronda se presentará una selección aleatoria de <strong>hasta 10 preguntas</strong>.</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-[#00e676] font-bold">•</span>
                                <span>Cada respuesta correcta otorga <strong>10 puntos de destreza</strong>.</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-[#00e676] font-bold">•</span>
                                <span>Cuenta con un tiempo límite global de <strong>2 minutos y 30 segundos</strong> para responder.</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-[#00e676] font-bold">•</span>
                                <span>Al finalizar, se generará un código QR con sus puntos acumulados (hasta <strong>100 pts máx</strong>).</span>
                            </div>
                        </div>

                        <button
                            onClick={prepareAndStartGame}
                            className="w-full bg-[#00e676] text-[#07241c] py-4 px-6 rounded-2xl font-black uppercase tracking-widest text-sm md:text-base shadow-xl hover:scale-105 active:scale-95 transition-all"
                        >
                            ENTENDIDO / INICIAR JUEGO
                        </button>
                    </div>
                </div>
            )}

            {/* Juego en Progreso (Playing) */}
            {gameState === 'playing' && currentQ && (
                <main className="w-full max-w-5xl flex flex-col items-center justify-center my-auto z-10 space-y-6 px-2">
                    {/* Indicador de número de pregunta */}
                    <div className="text-xs md:text-sm font-black tracking-widest text-[#00e676] uppercase bg-[#0b382c] px-4 py-1.5 rounded-full border border-[#00e676]/30">
                        Pregunta {currentQuestionIndex + 1} de {questions.length}
                    </div>

                    {/* Banner / Recuadro de la Pregunta Principal con fondo_pregunta.png */}
                    <div className="w-full relative flex justify-center items-center">
                        <div
                            className="w-full max-w-3xl min-h-[140px] md:min-h-[180px] p-8 md:p-12 text-[#07241c] text-center flex items-center justify-center bg-contain bg-center bg-no-repeat transition-all drop-shadow-2xl"
                            style={{ backgroundImage: `url(${FTP_TRIVIA_BASE}/fondo_pregunta.png)` }}
                        >
                            <h2 className="text-base md:text-xl font-extrabold text-[#07241c] leading-snug px-6 md:px-12 max-w-2xl">
                                {currentQ.question}
                            </h2>
                        </div>
                    </div>

                    {/* Cuadrícula 2x2 de Opciones de Respuesta con los PNGs Originales Exactos */}
                    <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5 pt-2">
                        {(currentQ.options || []).map((opt, idx) => {
                            const isSelected = selectedOptionId === opt.id;
                            const optionLetter = lettersMap[idx] || `${idx + 1}`;

                            const bgImage = isSelected
                                ? `${FTP_TRIVIA_BASE}/fondo_respuesta_selected.png`
                                : `${FTP_TRIVIA_BASE}/fondo_respuesta_no_selected.png`;

                            const letterBgImage = isSelected
                                ? `${FTP_TRIVIA_BASE}/letra_respuesta_selected.png`
                                : `${FTP_TRIVIA_BASE}/letra_respuesta_no_selected.png`;

                            return (
                                <button
                                    key={opt.id}
                                    onClick={() => handleSelectOption(opt.id)}
                                    className={`relative w-full min-h-[60px] md:min-h-[70px] flex items-center transition-all select-none cursor-pointer group text-left ${
                                        isSelected
                                            ? 'scale-[1.02] drop-shadow-[0_0_15px_rgba(0,230,118,0.8)]'
                                            : 'hover:scale-[1.01] opacity-95 hover:opacity-100'
                                    }`}
                                >
                                    {/* PNG de Fondo de Respuesta Original (con 9-slice / fill completo) */}
                                    <div
                                        className="absolute inset-0 w-full h-full bg-contain bg-left bg-no-repeat pointer-events-none"
                                        style={{ backgroundImage: `url(${bgImage})`, backgroundSize: '100% 100%' }}
                                    />

                                    {/* Capa de Contenido: Letra PNG + Texto */}
                                    <div className="relative z-10 flex items-center w-full h-full px-2 py-2">
                                        {/* Insignia PNG de la Letra */}
                                        <div
                                            className="w-14 h-11 md:w-16 md:h-12 flex items-center justify-center bg-contain bg-center bg-no-repeat shrink-0 mr-3"
                                            style={{ backgroundImage: `url(${letterBgImage})`, backgroundSize: 'contain' }}
                                        >
                                            <span className={`text-lg md:text-xl font-black ${isSelected ? 'text-[#00e676]' : 'text-[#07241c]'}`}>
                                                {optionLetter}.
                                            </span>
                                        </div>

                                        {/* Texto de la Respuesta */}
                                        <span
                                            className={`text-xs md:text-sm font-extrabold leading-snug pr-6 transition-colors ${
                                                isSelected ? 'text-[#07241c]' : 'text-slate-100'
                                            }`}
                                        >
                                            {opt.option_text}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Botones de Navegación entre Preguntas */}
                    <div className="w-full max-w-4xl flex justify-between items-center pt-4">
                        <button
                            onClick={handlePrevQuestion}
                            disabled={currentQuestionIndex === 0}
                            className={`px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-wider transition-all ${currentQuestionIndex === 0
                                ? 'opacity-40 cursor-not-allowed bg-slate-800 text-slate-500'
                                : 'bg-[#0b382c] hover:bg-[#00e676] hover:text-[#07241c] text-white border border-[#00e676]/40'
                                }`}
                        >
                            ← Anterior
                        </button>

                        <button
                            onClick={handleNextQuestion}
                            className="bg-[#00e676] hover:bg-[#00c853] text-[#07241c] px-8 py-3.5 rounded-xl font-black text-xs md:text-sm uppercase tracking-widest shadow-lg hover:scale-105 active:scale-95 transition-all"
                        >
                            {currentQuestionIndex < questions.length - 1 ? 'Siguiente →' : 'Finalizar Trivia ✓'}
                        </button>
                    </div>
                </main>
            )}

            {/* Pantalla Final: Generación de Código QR y Resumen */}
            {gameState === 'completed' && (
                <main className="w-full max-w-2xl flex flex-col items-center justify-center my-auto z-10 text-center space-y-6 bg-[#0b382c] border-2 border-[#00e676] p-8 rounded-3xl backdrop-blur-md shadow-2xl animate-fadeIn">
                    <div className="text-5xl">🏁</div>
                    <h2 className="text-2xl md:text-3xl font-black text-[#00e676] uppercase tracking-wider">
                        ¡TRIVIA FINALIZADA!
                    </h2>

                    <div className="bg-[#07241c] p-4 rounded-2xl border border-[#00e676]/30 space-y-2 w-full max-w-md">
                        <p className="text-xs text-slate-300">
                            Respuestas Correctas: <span className="font-bold text-[#00e676]">{results.correctCount} de {questions.length}</span>
                        </p>
                        <p className="text-base font-black text-amber-400">
                            ⭐ Puntos Obtenidos: {results.totalPoints} pts
                        </p>
                    </div>

                    {/* Código QR Generado al Finalizar */}
                    <div className="flex flex-col items-center bg-white p-6 rounded-3xl shadow-2xl space-y-3">
                        <QRCodeSVG
                            value={qrPayload}
                            size={200}
                            bgColor={"#ffffff"}
                            fgColor={"#07241c"}
                            level={"H"}
                            includeMargin={false}
                        />
                        <p className="text-[11px] font-bold text-[#07241c] uppercase tracking-wider">
                            Escanee para reclamar sus puntos
                        </p>
                    </div>

                    <button
                        onClick={handleRestartClick}
                        className="bg-[#00e676] text-[#07241c] py-3.5 px-8 rounded-xl font-black uppercase tracking-widest text-xs md:text-sm shadow-lg hover:scale-105 active:scale-95 transition-all"
                    >
                        REINICIAR TRIVIA
                    </button>
                </main>
            )}

            {/* Footer con Logo Corporativo */}
            <footer className="w-full max-w-6xl flex justify-between items-center pt-2 pb-1 z-10 px-4">
                <img
                    src={`${FTP_TRIVIA_BASE}/logo.png`}
                    alt="Boehringer Ingelheim"
                    className="h-8 object-contain rounded-sm"
                />
            </footer>
        </div>
    );
};

export default FocusTrivia;
